import React from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Plane, Building2, User, LogIn, Menu, X, Sparkles } from 'lucide-react';

/**
 * PublicLayout - Layout for pages accessible without login
 * Provides navigation bar with option to login
 */
const PublicLayout = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-[#030014]">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#030014]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo - Text only */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-white font-bold text-lg">AIRA ECHO</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <Link 
                to="/travel" 
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              >
                <Plane className="w-4 h-4" />
                <span>Flights</span>
              </Link>
              <Link 
                to="/stays" 
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              >
                <Building2 className="w-4 h-4" />
                <span>Hotels</span>
              </Link>
              
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
                >
                  <User className="w-4 h-4" />
                  <span>{user?.name?.split(' ')[0] || 'Dashboard'}</span>
                </button>
              ) : (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
                >
                  <User className="w-4 h-4" />
                  <span>Dashboard</span>
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white/70 hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#030014]/95 backdrop-blur-xl border-t border-white/5">
            <div className="px-4 py-4 space-y-4">
              <Link 
                to="/travel" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors py-2"
              >
                <Plane className="w-4 h-4" />
                <span>Flights</span>
              </Link>
              <Link 
                to="/stays" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors py-2"
              >
                <Building2 className="w-4 h-4" />
                <span>Hotels</span>
              </Link>
              
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/dashboard');
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 rounded-lg"
                >
                  <User className="w-4 h-4" />
                  <span>Go to Dashboard</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/dashboard');
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 rounded-lg"
                >
                  <User className="w-4 h-4" />
                  <span>Dashboard</span>
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content - with top padding for fixed navbar */}
      <main className="pt-16">
        <Outlet />
      </main>
    </div>
  );
};

export default PublicLayout;
